import { useEffect, useRef, useState } from 'react'
import { com, kotlin } from '@eidu/form'
import { KindEditPageProps } from '../KindPages'
import PageLayout from '../shared/PageLayout'
import EntityFieldInputGroup from '../shared/EntityFieldInputGroup'
import LoadingOverlay from '../../../../components/feedback/LoadingOverlay'
import PermissionError from '../../PermissionError'
import FormContentInput from '../../../../components/input/FormContentInput'
import getFormContent from '../../../../io/api/form/getFormContent'
import postFormContent from '../../../../io/api/form/postFormContent'
import postForm from '../../../../io/api/form/postForm'
import { draftFieldValuesToFieldValues } from '../../../../domain/entity/DraftFieldValue'
import { logException } from '../../../../util/Logging'
import useEntityEdit from '../shared/useEntityEdit'
import { useFormErrors } from '../../../../util/FormErrorBus'
import computeLabel from '../shared/computeLabel'
import Link from '../../../../domain/entity/Link'
import KtList = kotlin.collections.KtList
import Page = com.eidu.sharedlib.form.definition.Page
import getFormContentId = com.eidu.sharedlib.entity.getFormContentId
import FormContent = com.eidu.sharedlib.form.FormContent
import NonEmptyList = com.quickbirdstudios.nonEmptyCollection.list.NonEmptyList
import FormValidator = com.eidu.sharedlib.form.validation.FormValidator
import toException = com.eidu.sharedlib.form.validation.toException
import formContentIdKey = com.eidu.sharedlib.entity.formContentIdKey
import formContentHashKey = com.eidu.sharedlib.entity.formContentHashKey
import EntityId = com.eidu.sharedlib.entity.EntityId

const FormEditPage = ({ entity, entityId, entityType, entityTypes, entityRepository, loading }: KindEditPageProps) => {
  const { hasErrors: hasFormErrors } = useFormErrors()

  const {
    fields,
    updateField,
    submitting,
    valid: fieldsValid,
    submitEntity,
    deleteEntity,
    hasPermissions,
    permissionsLoading,
    registerValidation,
    registerPostSubmit,
  } = useEntityEdit({ entity, entityId, entityType, entityRepository })

  const [formContent, setFormContent] = useState<KtList<Page> | undefined>(undefined)
  const [formContentLoading, setFormContentLoading] = useState(false)
  const [formContentErrorMessage, setFormContentErrorMessage] = useState<string>()

  const entityIdString = entity?.entity.id.toString()
  const formContentId = entity ? getFormContentId(entity.entity)?.toString() : undefined

  useEffect(() => {
    if (entityIdString && formContentId) {
      setFormContentLoading(true)
      getFormContent({ formId: entityIdString, formContentId })
        .then((content) => {
          const formContentWithPageKeys = KtList.fromJsArray(
            content
              .asJsReadonlyArrayView()
              .map((page: Page, index: number) =>
                !page.key ? page.copy(page.title, page.items, `page_${Date.now()}_${index}`) : page
              )
          )
          setFormContent(formContentWithPageKeys)
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : String(err)
          setFormContentErrorMessage(message)
          setFormContent(undefined)
          logException(err)
        })
        .finally(() => setFormContentLoading(false))
    } else if (!entityIdString) {
      setFormContent(
        KtList.fromJsArray([new Page('Untitled page', KtList.fromJsArray([]), `page_${Date.now()}`, null)])
      )
    }
  }, [entityIdString, formContentId])

  const formContentRef = useRef(formContent)
  formContentRef.current = formContent

  useEffect(() => {
    registerValidation(() => !hasFormErrors)
  }, [hasFormErrors, registerValidation])

  useEffect(() => {
    registerPostSubmit(async (savedEntityId: EntityId) => {
      const pages = formContentRef.current
      if (!pages) throw Error('Unable to save form: form content is undefined')

      const validationErrors = FormValidator.validatePages(KtList.fromJsArray(pages.asJsReadonlyArrayView()))
      if (validationErrors.asJsReadonlyArrayView().length > 0) {
        throw toException(validationErrors)
      }

      const contentIdAndHash = entity
        ? await postFormContent({
            formId: entity.entity.id.toString(),
            content: new FormContent(crypto.randomUUID(), NonEmptyList.fromArray([...pages.asJsReadonlyArrayView()])),
          })
        : await postForm({
            formId: savedEntityId.toString(),
            initialContent: new FormContent(
              crypto.randomUUID(),
              NonEmptyList.fromArray([...pages.asJsReadonlyArrayView()])
            ),
          })

      const links: Link[] = [
        { key: formContentIdKey, value: contentIdAndHash.contentId.toString() },
        { key: formContentHashKey, value: contentIdAndHash.contentSha256 },
      ]
      await entityRepository.modifyEntity(savedEntityId, {
        valuesToUpdateByFieldId: draftFieldValuesToFieldValues([]),
        linksToUpdate: new Map(links.map((link) => [link.key, link.value])),
        externalValuesToUpdate: new Map(),
      })
    })
  }, [entity, entityRepository, registerPostSubmit])

  const valid = fieldsValid && !hasFormErrors
  const loadingOverlayOpen = submitting || formContentLoading

  if (!hasPermissions && !loading && !permissionsLoading) {
    return <PermissionError missingPermittedActions={[]} />
  }

  const label = computeLabel(entityType, fields)

  return (
    <>
      <PageLayout
        title={label ?? entityType.name}
        subtitle={label ? entityType.name : undefined}
        showCancel
        showSave
        showDelete={!!entity}
        saveDisabled={!valid}
        onSave={submitEntity}
        onDelete={deleteEntity}
      >
        <EntityFieldInputGroup
          entityType={entityType}
          entityTypes={entityTypes}
          fields={fields}
          updateField={updateField}
          loading={loading}
        />
        {formContent && (
          <FormContentInput pages={formContent} onChange={setFormContent} errorMessage={formContentErrorMessage} />
        )}
      </PageLayout>
      <LoadingOverlay isOpen={loadingOverlayOpen} />
    </>
  )
}

export default FormEditPage
